import React, { useEffect, useState } from 'react';
import { Route, RouteComponentProps } from 'react-router-dom';
import styled from '@emotion/styled';
import { Helmet } from 'react-helmet-async';
import gql from 'graphql-tag';
import { useQuery, useMutation } from '@apollo/client';

import { useDispatch, useCurrency, useSelector, useI18n, useLogger, useLicenses } from '~Hooks';
import { Zone, CountryCode, Languages } from '~Types';

import Tables from '../Tables';
import ParkedOrders from '../ParkedOrders';
import Reports from '../Reports';
import History from '../History';
import MainMenu from '../../Components/MainMenu';
import ZReportsNew from '../Zreports';
import Gateway from '../Gateway';
import EventedKitchenDisplay from '../EventedKitchenDisplay';
import CustomerDisplay from '../CustomerDisplay';
import Inventory from '../Inventory-v2';
import MultiQSR from '../MultiQSR';
import SimplePAT from '../SimplePAT';
import EndOfDay from '../EndOfDay';
import InventoryOrder from '../Inventory/Order';
import { Navbar } from '../../Components/Navbar';
import { checkScreen } from '~utilities';
import * as actions from '../../actions';
import SelfServiceKiosks from '../SelfServiceKiosks';
import CashSources from '../CashSources';
import AdyenExpense from '../AdyenExpense';
import QSR from '~Scenes/QSR';
import Members from '~Scenes/Members';
import GiftCards from '~Scenes/GiftCards';
import IngredientInventory from '~Scenes/Inventory/Ingredients';
import ReceiptPrinter from '~Scenes/Tables/Components/ReceiptPrinter';

import {
  getCompany,
  getCompanyVariables,
  getIntegrations,
  getIntegrationsVariables,
} from './__queries__';
import Synchronizers from '~/Components/Synchronizers';
import { useStorage } from '~Components/Storage';
import HandyWrapper from '~Scenes/Handy';
import Competitions from '~Scenes/Competitions';
import SettingsV2 from '~Scenes/Settings-v2';
import { PaymentReminder } from '~ComponentsImproved/PaymentReminder';
import Transactions from '~Scenes/Transactions';
import AccessHandler from '~Providers/LicensesProvider/Components/AccessHandler';
import Standalone from '~Scenes/Standalone';
import HistoryV2 from '~Scenes/History-v2';
import NoDataModal from './Components/NoDataModal';
import { BOOKING_DISCOUNT_CATEGORIES_QUERY } from '~Providers/BookingsProvider/api/queries';
import { GetBookingDiscountCategoryIds } from '~Providers/BookingsProvider/api/__queries__';
import NotifyReleaseModal from '~Scenes/Gateway/Components/NotifyReleaseModal';
import StaffShiftsHandler from '../../Providers/StaffShiftsProvider/Components/StaffShiftsHandler';
import StaffShifts from '~Scenes/StaffShifts';

const Wrapper = styled.div<{
  isShowMainMenu: boolean;
  visibleNavbar: boolean;
}>`
  height: 100%;
  overflow: ${(props) => (props.isShowMainMenu ? 'hidden' : 'visible')};
  padding-bottom: ${(props) => (props.visibleNavbar ? '64px' : '0px')};
`;

const GET_COMPANY = gql`
  query getCompany($venueId: ID!) {
    Venue(id: $venueId) {
      id
      beeperList
      enableAutoCashEOD
      hasIndividualEOD
      discountExcludedCategoryIds
      company {
        id
        name
        defaultCurrency
        defaultLanguage
        timezone
        country

        staffs {
          id
          name
          code
          ssn
          accessLevel {
            id
            name
            permissions
          }
        }

        predefinedReasons {
          id
          text
          type
        }

        stations {
          id
          name
          categories {
            id
            name
          }
          items {
            id
            title
          }
          orderStatuses
          origin
          serviceType
          showAllItems
        }

        zones {
          id
          name
          createdAt
          deleted
          tables {
            id
          }
        }

        courseTypes {
          id
          label
          name
          color
          order
        }
      }
    }
  }
`;

const GET_INTEGRATIONS = gql`
  query getIntegrations($companyId: ID!) {
    getIntegrations(companyId: $companyId) {
      success
      integrations
      message
    }
  }
`;

const VENUE_HEART_BEAT = gql`
  mutation venueHeartbeat($venueId: ID!, $version: String, $deviceId: String, $deviceOS: String) {
    venueHeartbeat(
      venueId: $venueId
      version: $version
      deviceId: $deviceId
      deviceOS: $deviceOS
    ) {
      success
    }
  }
`;

interface MatchParams {
  id: string;
}

type Props = RouteComponentProps<MatchParams>;

const Venue = ({ match, location }: Props) => {
  const i18n = useI18n();
  const currency = useCurrency();
  const dispatch = useDispatch();
  const licenses = useLicenses();
  const [storage, updateStorage, storageLoading] = useStorage();

  const navigationLogger = useLogger('navigation');
  const venueLogger = useLogger('venue');

  const [visibleCurrentProduct, setVisibleCurrentProduct] = useState(false);
  const [visibleNavbar, setVisibleNavbar] = useState(true);
  const [scaleUi, setScaleUi] = useState(false);
  const [languageSet, setLanguageSet] = useState(false);

  const venueId = match.params.id;
  const isShowMainMenu = useSelector((state) => state.mainMenu);
  const appVersion = useSelector((state) => state.appVersion);
  const deviceOS = useSelector((state) => state.deviceOS);

  useEffect(() => {
    navigationLogger.info('Navigated: ' + location.pathname, {
      venueId,
    });
  }, [location.pathname]);

  const [heartbeat] = useMutation(VENUE_HEART_BEAT, {
    variables: {
      venueId,
      version: appVersion,
      deviceId: storage.taxComplianceDeviceId,
      deviceOS,
    },
  });

  const companyQuery = useQuery<getCompany, getCompanyVariables>(GET_COMPANY, {
    variables: {
      venueId,
    },
    fetchPolicy: 'cache-and-network',
  });

  const companyId = companyQuery.data?.Venue?.company?.id ?? '';

  const integrationsQuery = useQuery<getIntegrations, getIntegrationsVariables>(GET_INTEGRATIONS, {
    variables: {
      companyId: companyId,
    },
    skip: !companyId,
  });

  const bookingDiscountQuery = useQuery<GetBookingDiscountCategoryIds>(
    BOOKING_DISCOUNT_CATEGORIES_QUERY,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    dispatch({
      type: actions.SET_VENUE_ID,
      venueId,
    });

    const heartbeatInterval = setInterval(() => {
      if (!__DEV__) {
        heartbeat();
      }
    }, 60000);
    return () => clearInterval(heartbeatInterval);
  }, []);

  useEffect(() => {
    if (!companyQuery.error) {
      return;
    }

    venueLogger.error('Error fetching company data', {
      errorContent: companyQuery.error,
    });
  }, [companyQuery.error]);

  useEffect(() => {
    if (!companyQuery.data) {
      return;
    }

    const companyData = companyQuery.data;
    const company = companyData?.Venue?.company;

    if (!company) {
      venueLogger.error('No company data available');
      return;
    }

    venueLogger.info('DEBUG currency: ', {
      currentCode: currency.code,
      data: companyData,
    });

    dispatch(actions.setCompanyTimezone(company.timezone ?? 'Europe/Stockholm'));
    dispatch(actions.setCompany(companyId));
    dispatch(actions.setCompanyName(company.name));
    dispatch(actions.setCountry((company.country as CountryCode) ?? null));
    dispatch(actions.setDefaultLanguage((company.defaultLanguage as Languages) ?? null));

    dispatch(actions.setBeeperList(companyData?.Venue?.beeperList ?? []));
    dispatch(actions.setPredefinedReasons(company.predefinedReasons ?? []));

    dispatch(actions.setEnableAutoCashEOD(companyData?.Venue?.enableAutoCashEOD ?? false));
    dispatch(
      actions.setHasIndividualEOD(
        (companyData?.Venue?.hasIndividualEOD || companyData?.Venue?.enableAutoCashEOD) ?? false,
      ),
    );

    updateStorage({
      currency: {
        code: company.defaultCurrency,
        symbol: currency.symbol(company.defaultCurrency),
      },
    });

    const companyStaffs = company.staffs;
    const companyStations = company.stations;
    const companyZones = company.zones;
    const companyCourseTypes = company.courseTypes;

    if (companyCourseTypes) {
      dispatch(actions.setCourseTypes(companyCourseTypes));
    }

    if (companyStaffs) {
      dispatch(actions.setCompanyStaff(companyId, companyStaffs));
    }

    if (companyStations) {
      const stations = companyStations.map((station) => ({
        id: station.id,
        name: station.name,
        items: station.items.map(({ id }) => id),
        categories: station.categories.map(({ id, name }) => ({ id, name })),
        orderStatuses: station.orderStatuses,
        origins: station.origin,
        serviceTypes: station.serviceType,
        showAllItems: station.showAllItems,
      }));
      dispatch(actions.setStations(stations));

      const zones: Zone[] = companyZones.map((zone: Zone) => ({
        id: zone.id,
        name: zone.name,
        createdAt: zone.createdAt,
        deleted: zone.deleted,
        tables: zone.tables,
      }));

      dispatch(actions.setZones(zones));
    }
  }, [companyQuery.data]);

  useEffect(() => {
    if (!bookingDiscountQuery.data) {
      return;
    }

    const categoryIds =
      bookingDiscountQuery.data.operativeViewer?.bookingDiscountCategoryIds || [];

    dispatch(actions.setBookingDiscountCategoryIds(categoryIds));
  }, [bookingDiscountQuery.data]);

  useEffect(() => {
    if (languageSet || storageLoading || !companyQuery.data) {
      return;
    }

    const companyLanguage =
      storage.selectedLanguage ?? companyQuery.data?.Venue?.company?.defaultLanguage;

    if (companyLanguage) {
      i18n.changeLanguage(companyLanguage);
      setLanguageSet(true);
    }
  }, [storageLoading, companyQuery.data]);

  useEffect(() => {
    if (!integrationsQuery.data) {
      return;
    }

    const { getIntegrations: activeIntegrations } = integrationsQuery.data;

    if (!activeIntegrations) {
      return;
    }

    const { integrations, success } = activeIntegrations;

    if (!success || !integrations) {
      venueLogger.error('Failed to fetch integrations', {
        errorMessage: integrationsQuery.data?.getIntegrations?.message || integrationsQuery.error,
      });
      return;
    }

    if (integrations.companyIntegrations.length > 0) {
      dispatch(actions.setActiveIntegrations(integrations.companyIntegrations));
    }
  }, [integrationsQuery.data]);

  // Scale down UI in QSR and TSR mode on smaller screens
  // 1366px is the screen.width of an iPad Pro which we have designed for
  // const isSmallerScreen = screen && screen.width < 1366;

  useEffect(() => {
    setScaleUi(false);

    switch (true) {
      // settings must be checked before 'tables' since settings-v2/tables contains 'tables'
      case checkScreen(location.pathname, 'settings'):
        setScaleUi(true);
        setVisibleNavbar(true);
        break;

      case checkScreen(location.pathname, 'orders'):
        setVisibleNavbar(true);
        /* Delay the change to avoid flashing in UI */
        setTimeout(() => setVisibleCurrentProduct(true), 500);
        break;

      case checkScreen(location.pathname, 'evented-kitchen-display'):
      case checkScreen(location.pathname, 'self-service-kiosks'):
      case checkScreen(location.pathname, 'express'):
        setScaleUi(true);
      // fallsthrough
      case checkScreen(location.pathname, 'kitchen-display'):
      case checkScreen(location.pathname, 'pos'):
      case checkScreen(location.pathname, 'customer-display'):
      case checkScreen(location.pathname, 'tables'):
      case checkScreen(location.pathname, 'order-ready-display'):
      case checkScreen(location.pathname, 'simple-pat'):
      case checkScreen(location.pathname, 'handy'):
      case checkScreen(location.pathname, 'standalone'):
        setVisibleNavbar(false);
        /* Delay the change to avoid flashing in UI */
        setTimeout(() => setVisibleCurrentProduct(true), 500);
        break;

      default:
        setVisibleNavbar(true);
        /* Delay the change to avoid flashing in UI */
        setTimeout(() => setVisibleCurrentProduct(false), 500);
        break;
    }
  }, [location]);

  const landScapeElo = screen && screen.width >= 1280;
  const portraitElo = screen && screen.width <= 720;

  const isImportantDataMissing =
    (!companyQuery.data && !companyQuery.loading) || licenses.hasQueryFailed;

  return (
    <>
      {scaleUi && (
        <Helmet>
          {landScapeElo && <meta name="viewport" content="width=1920, user-scalable=0" />}
          {portraitElo && <meta name="viewport" content="width=1080, user-scalable=0" />}
        </Helmet>
      )}
      {venueId && <ReceiptPrinter venueId={venueId} />}
      <Wrapper isShowMainMenu={isShowMainMenu} visibleNavbar={visibleNavbar}>
        <MainMenu visibleCurrentProduct={visibleCurrentProduct} id={match.params.id} />
        <Route path="/venue/:id/orders/:type?" component={Gateway} />
        <Route path="/venue/:id/history" component={History} />
        <Route path="/venue/:id/history-v2" component={HistoryV2} />
        <Route path="/venue/:id/gift-cards/:id?" component={GiftCards} />
        <Route path="/venue/:id/tables" component={Tables} />
        <Route path="/venue/:id/standalone" component={Standalone} />
        <Route path="/venue/:id/pos" component={QSR} />
        <Route path="/venue/:id/transactions" component={Transactions} />
        <Route path="/venue/:id/pos/parked-orders/" component={ParkedOrders} />
        <Route path="/venue/:id/multi-pos" component={MultiQSR} />
        <Route path="/venue/:id/multi-pos/parked-orders/" component={ParkedOrders} />
        <Route path="/venue/:id/reports" component={Reports} />
        <Route exact path="/venue/:id/settings-v2" component={SettingsV2} />
        <Route path="/venue/:id/settings-v2/*" component={SettingsV2} />
        <Route path="/venue/:id/z-reports-new" component={ZReportsNew} />
        <Route path="/venue/:id/cash-sources" component={CashSources} />
        <Route path="/venue/:id/staff-shifts" component={StaffShifts} />
        <Route path="/venue/:id/members" component={Members} />
        <Route path="/venue/:id/expenses" component={AdyenExpense} />
        <Route path="/venue/:id/newReports" component={Reports} />
        <Route path="/venue/:id/gateway" component={Gateway} />
        <Route path="/venue/:id/simple-pat" component={SimplePAT} />
        <Route path="/venue/:id/kitchen-display" component={EventedKitchenDisplay} />
        <Route path="/venue/:id/evented-kitchen-display">
          <EventedKitchenDisplay venueId={venueId} />
        </Route>
        <Route path="/venue/:id/self-service-kiosks" component={SelfServiceKiosks} />
        <Route
          exact
          path="/venue/:id/customer-display-responsive"
          component={() => <CustomerDisplay useResponsiveDisplay />}
        />
        <Route exact path="/venue/:id/customer-display" component={() => <CustomerDisplay />} />

        <Route
          path="/venue/:id/order-ready-display"
          component={() => <CustomerDisplay useResponsiveDisplay />}
        />
        <Route path="/venue/:id/inventory" component={Inventory} />
        <Route path="/venue/:id/ingredient-inventory" component={IngredientInventory} />
        <Route path="/venue/:id/end-of-day" component={EndOfDay} />
        <Route path="/venue/:id/inventory-order" component={InventoryOrder} />
        <Route path="/venue/:id/handy" component={HandyWrapper} />
        <Route path="/venue/:id/competitions" component={Competitions} />
        <Synchronizers venueId={venueId} />
        <PaymentReminder />
        {visibleNavbar && <Navbar />}
      </Wrapper>

      <AccessHandler />
      <NotifyReleaseModal />

      {licenses.hasStaffLedger && <StaffShiftsHandler />}

      {isImportantDataMissing && <NoDataModal />}
    </>
  );
};

export default Venue;
